<template>
	<div></div>
</template>

<script setup lang="ts">
import { BlexonColor } from "@/model/BlexonColor";
import { EditorMode } from "@/model/Enums";
import { IWeldlineDto } from "@/model/IWeldlineDto";
import { useMainStore } from "@/store/mainStore";
import * as THREE from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry";
import { watch, onMounted, onUnmounted } from "vue";

interface IWeldlineDataContainer {
	geometry: THREE.BufferGeometry;
	start: THREE.Vector3;
	end: THREE.Vector3;
}

const getPointOnLine = function pointOnLine(
	a: THREE.Vector3,
	b: THREE.Vector3,
	distance: number
): THREE.Vector3 {
	// Compute the direction vector
	const direction = new THREE.Vector3().subVectors(b, a).normalize();

	// Scale by the desired distance
	const scaledDirection = direction.multiplyScalar(distance);

	// Compute the new point
	const point = new THREE.Vector3().addVectors(a, scaledDirection);

	return point;
};

const store = useMainStore();

watch(store.weldlines, () => {
	showOrHide();
});

const weldlineColor = new THREE.Color(BlexonColor.weldlineColor);
const weldlineSelectedColor = new THREE.Color(BlexonColor.selectionColor);
const weldlineHoverColor = new THREE.Color(BlexonColor.hoveredGeometryColor);

const showOrHide = async () => {
	if (!store.cam.sceneGroup) {
		return;
	}

	// remove old weldlines
	const previousWeldlines = store.cam.sceneGroup.children.filter((c) => c.name === "Weldline");
	for (const child of previousWeldlines) {
		store.cam.sceneGroup.remove(child);
	}

	if (store.editorMode != EditorMode.WeldMode) {
		return;
	}

	for (const weldline of store.weldlines) {
		const material = new THREE.MeshBasicMaterial();
		material.color = weldlineColor;
		if (weldline.hovered) {
			material.color = weldlineHoverColor;
		} else if (weldline.selected) {
			material.color = weldlineSelectedColor;
		}
		if (weldline.version === 3 || (weldline.version === 4 && weldline.isMesh)) {
			// Version 3 is face to face but contains the tesselation in the curve property
			if (weldline.curve && weldline.curve.length > 3) {
				const geometry = new ConvexGeometry(weldline.curve);
				const mesh = createWeldlineMesh(geometry, material, null, weldline.weldlineId);
				//mesh.position.add(translation)
				store.cam.sceneGroup.add(mesh);
			} else {
				return;
			}
		} else if (weldline.version === 4 && weldline.curve) {
			for (const geometry of getGeometries(weldline)) {
				const mesh = createWeldlineMesh(
					geometry.geometry,
					material,
					null,
					weldline.weldlineId
				);
				store.cam.sceneGroup.add(mesh);

				if (geometry.start) {
					const sphereGeometry = new THREE.SphereGeometry(weldline.distance / 2, 48, 48);
					const mesh = createWeldlineMesh(
						sphereGeometry,
						material,
						geometry.start,
						weldline.weldlineId
					);
					store.cam.sceneGroup.add(mesh);
				}
				if (geometry.end) {
					const sphereGeometry = new THREE.SphereGeometry(weldline.distance / 2, 48, 48);
					const mesh = createWeldlineMesh(
						sphereGeometry,
						material,
						geometry.end,
						weldline.weldlineId
					);
					store.cam.sceneGroup.add(mesh);
				}
			}
		}

		if (weldline.weldCorners && weldline.weldCorners.length > 0) {
			for (const corner of weldline.weldCorners) {
				if (corner.cornerPoints.length > 3 && corner.weldCorner) {
					const geometry = new ConvexGeometry(corner.cornerPoints);
					const cornerMesh = new THREE.Mesh(geometry, material);
					cornerMesh.name = "Weldline";
					store.cam.sceneGroup.add(cornerMesh);
				}
			}
		}
	}
};

const createWeldlineMesh = (
	geometry: THREE.BufferGeometry,
	material: THREE.MeshBasicMaterial,
	position: THREE.Vector3 | null,
	id: number | null
) => {
	const mesh = new THREE.Mesh(geometry, material);
	if (position) {
		mesh.position.set(position.x, position.y, position.z);
	}
	mesh.name = "Weldline";
	mesh.userData = { id: id };
	mesh.receiveShadow = true;
	mesh.castShadow = true;
	return mesh;
};

const getGeometries = (weldline: IWeldlineDto) => {
	const segments: IWeldlineDataContainer[] = [];
	const getGeometry = (start: THREE.Vector3, end: THREE.Vector3): IWeldlineDataContainer => {
		const curve = new THREE.CatmullRomCurve3([start, end]);
		const tubeGeometry = new THREE.TubeGeometry(curve, 100, weldline.distance / 2, 8, true);
		return {
			geometry: tubeGeometry,
			start: start,
			end: end,
		};
	};
	if (weldline.curve) {
		if (
			weldline.curve.length === 2 &&
			weldline.numberOfSegments >= 2 &&
			weldline.length > weldline.lengthOfSegment * weldline.numberOfSegments
		) {
			// Line
			// Startsegment
			let start = weldline.curve[0];
			let end = getPointOnLine(
				weldline.curve[0],
				weldline.curve[1],
				weldline.lengthOfSegment
			);
			segments.push(getGeometry(start, end));

			const intervall =
				(weldline.length - weldline.lengthOfSegment) / (weldline.numberOfSegments - 1);
			for (let i = 0; i < weldline.numberOfSegments - 2; i++) {
				start = getPointOnLine(
					end,
					weldline.curve[1],
					intervall - weldline.lengthOfSegment
				);
				end = getPointOnLine(start, weldline.curve[1], weldline.lengthOfSegment);
				segments.push(getGeometry(start, end));
			}

			// Endsegment
			start = weldline.curve[1];
			end = getPointOnLine(weldline.curve[1], weldline.curve[0], weldline.lengthOfSegment);
			segments.push(getGeometry(start, end));
		} else {
			// Curve oder keine Segmente oder Länge zu kurz für Segmente
			const copyOfCurve = [];
			for (const entry of weldline.curve) {
				copyOfCurve.push(new THREE.Vector3(entry.x, entry.y, entry.z));
			}
			const originalStart = copyOfCurve[0];
			const originalEnd = copyOfCurve[copyOfCurve.length - 1];
			const startOffset = getPointOnLine(originalStart, originalEnd, weldline.distance / 2);
			const endOffset = getPointOnLine(originalEnd, originalStart, weldline.distance / 2);
			copyOfCurve[0].x = startOffset.x;
			copyOfCurve[0].y = startOffset.y;
			copyOfCurve[0].z = startOffset.z;
			copyOfCurve[copyOfCurve.length - 1].x = endOffset.x;
			copyOfCurve[copyOfCurve.length - 1].y = endOffset.y;
			copyOfCurve[copyOfCurve.length - 1].z = endOffset.z;

			const catmullCurve = new THREE.CatmullRomCurve3(copyOfCurve);
			const tubeGeometry = new THREE.TubeGeometry(
				catmullCurve,
				100,
				weldline.distance / 2,
				8,
				false
			);

			segments.push({ geometry: tubeGeometry, start: startOffset, end: endOffset });
		}
	}
	return segments;
};

onMounted(() => {
	showOrHide();
});

onUnmounted(showOrHide);

watch(() => store.editorMode, showOrHide);
</script>
